import {
  Business,
  UnauthedBusiness,
  QueryBusinessArgs as QueryBusinessArgsBase,
  QueryBusinessesArgs as QueryBusinessesArgsBase,
  MutationUpdateBusinessArgs as MutationUpdateBusinessArgsBase,
  MutationCreateBusinessArgs as MutationCreateBusinessArgsBase,
  BusinessRestrictions
} from "@irati/types"
import client, {QueryOptions, MutationOptions} from "@irati/utils/services/apollo"
import PBService from "@irati/utils/services"
import getTenantFromPath from "../helpers/getTenantFromPath"
import userService from "./users"
import businessBySubdomain, {businessBySubdomainKey} from "./queries/businessBySubdomain"
import businessQuery, {businessKey} from "./queries/business"
import businessesQuery, {businessesKey} from "./queries/businesses"
import businessCreateMutation, {businessCreateKey} from "./mutations/businessCreate"
import businessUpdateMutation, {businessUpdateKey} from "./mutations/businessUpdate"
import businessRestrictions, {businessRestrictionsKey} from "./queries/businessRestrictions"

import coreErrorHandler from "./errors/coreErrorHandler"
import tenantErrorHandler from "./errors/legacy/tenantErrorHandler"

interface withAdminOrRootTeam {
  withAdmins?: boolean
  withRootTeam?: boolean
  withCreationMeta?: boolean
}

export interface QueryBusinessArgs extends QueryBusinessArgsBase, withAdminOrRootTeam {}
export interface QueryBusinessesArgs extends QueryBusinessesArgsBase, withAdminOrRootTeam {}
interface MutationCreateBusinessArgs extends MutationCreateBusinessArgsBase, withAdminOrRootTeam {}
interface MutationUpdateBusinessArgs extends MutationUpdateBusinessArgsBase, withAdminOrRootTeam {}

class TenantService extends PBService {
  get({businessId, withAdmins, withRootTeam, withCreationMeta}: QueryBusinessArgs, options: QueryOptions = {}): Promise<Business> {
    const variables = {
      businessId,
      withAdmins: withAdmins ?? false,
      withRootTeam: withRootTeam ?? false,
      withCreationMeta: withCreationMeta ?? false
    }

    return client.query({...options, query: businessQuery, variables}).then(({data}) => data[businessKey])
  }

  getBusinessBySubdomain = (subdomain: string = getTenantFromPath(), options: QueryOptions = {}): Promise<UnauthedBusiness> => {
    const variables = {subdomain}
    return client.query({...options, query: businessBySubdomain, variables}).then(({data}) => data[businessBySubdomainKey])
  }

  getBusinessRestrictions = (businessId: string, options: QueryOptions = {}): Promise<BusinessRestrictions> => {
    const variables = {businessId}
    return client.query({...options, query: businessRestrictions, variables}).then(({data}) => data[businessRestrictionsKey])
  }

  list(args: QueryBusinessesArgs, options: QueryOptions = {}) {
    const variables = args
    return client.query({...options, query: businessesQuery, variables}).then((res) => res.data[businessesKey])
  }

  create(
    {data, withAdmins, withRootTeam, withCreationMeta}: MutationCreateBusinessArgs,
    options: MutationOptions = {}
  ): Promise<Business> {
    const variables = {
      data,
      withAdmins: withAdmins ?? false,
      withRootTeam: withRootTeam ?? false,
      withCreationMeta: withCreationMeta ?? false
    }

    return client.mutate({...options, mutation: businessCreateMutation, variables}).then((res) => res.data[businessCreateKey])
  }

  update({data, withAdmins, withRootTeam, withCreationMeta}: MutationUpdateBusinessArgs, options: MutationOptions = {}) {
    const variables = {
      data,
      withAdmins: withAdmins ?? false,
      withRootTeam: withRootTeam ?? false,
      withCreationMeta: withCreationMeta ?? false
    }

    return client.mutate({...options, mutation: businessUpdateMutation, variables}).then((res) => res.data[businessUpdateKey])
  }

  userCount(businessId: string, options: QueryOptions = {}) {
    return userService.getUsersForBusiness({limit: 1, businessId}, options).then(({total}) => total)
  }
}

export default new TenantService({errorHandlers: [coreErrorHandler, tenantErrorHandler]})
